import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Top-level error boundary. Catches render crashes anywhere in the tree
 * and shows a recovery surface instead of a blank page. Logs the full
 * error (including component stack) so production telemetry can pick it
 * up via Sentry / similar later.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[web2] render crash', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary__panel">
          <span className="error-boundary__eyebrow">Something broke</span>
          <h1 className="error-boundary__title">We hit an unexpected error.</h1>
          <p className="error-boundary__sub">
            The app crashed while rendering this surface. Brain didn&apos;t lose anything you saved — refreshing usually clears it.
          </p>
          <pre className="error-boundary__detail">{this.state.error.message}</pre>
          <div className="error-boundary__actions">
            <button type="button" className="error-boundary__btn error-boundary__btn--primary" onClick={() => window.location.reload()}>
              Reload
            </button>
            <button type="button" className="error-boundary__btn" onClick={this.reset}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
